/*
 * Lot 12 : population allouée, redondance du maillage et trous de maillage. JS pur, testable en node.
 * Données : `snapshot.territoire.carreaux` (INSEE Filosofi 2019, carreaux 200 m, un point au centre : {lng, lat, pop}) ; l'index du
 * carreau dans ce tableau est la clé des distances à pied (`snapshot.chalandise.distances`, lot 13).
 * Lot 13 : `distancePav` lit la distance À PIED (OSRM foot, calculée à l'export) quand `chalandise.source === 'osrm-foot'`
 * (paire absente = hors portée = Infinity), sinon la distance à vol d'oiseau (haversine). Les voisines entre bornes
 * (`nbBornesAPortee`) restent à vol d'oiseau.
 * - `populationAPortee` : Σ pop des carreaux dont le centre est à moins de `rayonM` de la borne (une même personne peut
 *   être comptée pour plusieurs bornes voisines : ce n'est PAS une partition).
 * - `populationAllouee` (D1') : chaque carreau est attribué à la borne ACTIVE (statut != RETIRÉ) la plus proche si elle est
 *   à moins de `rayonM`, sinon à personne. Σ des populations allouées <= population totale : aucun double compte.
 * - `nbBornesAPortee` (D1'') : autres bornes actives à moins de `rayonM` ; `partAllouee` = allouée / à portée.
 * - Correction lot 12 : l'allocation se fait PAR GROUPE de patrimoine (`pav.groupe`, extra / intra) : un carreau est alloué à la
 *   borne la plus proche de chaque groupe (les bornes intra, au pied des immeubles, ne captent pas les carreaux des bornes extra).
 *   L'accessibilité par commune (`accessibiliteParCommune`) est calculée sur toutes les bornes actives, tous groupes.
 */

import {cleCommune} from './communeCle.js';
import {CIBLE_REMPLISSAGE} from '../tokens.js';

export const RAYONS = [100, 150, 250, 400];
export const RAYON_DEFAUT = 150; // seuil réglementaire urbain ; 100 = préconisé dense / touristique ; 250 et 400 = lecture de tolérance
export const RAYON_SEUIL_RURAL = 150; // rayon FIXE de la grille rurale (à pied si osrm-foot), indépendant du curseur
export const SEUIL_RURAL_HAB = 250; // grille rurale : habitants alloués par PAV (second indicateur, distinct du rayon)
const TAILLE_CASE = 0.01; // degrés : index spatial simple
const M_PAR_DEGRE_LAT = 111320;
const R_TERRE = 6371008.8;

/** Distance haversine en mètres. */
export function distanceM(lat1, lng1, lat2, lng2) {
    const rad = Math.PI / 180;
    const dLat = (lat2 - lat1) * rad;
    const dLng = (lng2 - lng1) * rad;
    const a = Math.sin(dLat / 2) ** 2 + Math.cos(lat1 * rad) * Math.cos(lat2 * rad) * Math.sin(dLng / 2) ** 2;
    return 2 * R_TERRE * Math.asin(Math.min(1, Math.sqrt(a)));
}

/** Distance PAV -> carreau : à pied si la chalandise est calculée (absente = Infinity), sinon à vol d'oiseau. */
export function distancePav(pav, carreau, indexCarreau, chalandise) {
    if (chalandise?.source === 'osrm-foot') {
        const d = chalandise.distances?.[String(pav.id)]?.[indexCarreau];
        return d === undefined || d === null ? Infinity : d;
    }
    return distanceM(pav.lat, pav.lng, carreau.lat, carreau.lng);
}

const cacheNonRattaches = new WeakMap();
const AUCUN = new Set();
/**
 * D9 : Set des index de carreaux « non rattachés » (centre à plus de 100 m de toute voirie piétonne), lus dans
 * `chalandise.nonRattaches` (liste triée posée par l'export quand `source === 'osrm-foot'`) ; absent -> ensemble vide.
 */
export function ensembleNonRattaches(chalandise) {
    if (!chalandise || !Array.isArray(chalandise.nonRattaches) || chalandise.nonRattaches.length === 0) return AUCUN;
    let e = cacheNonRattaches.get(chalandise);
    if (!e) cacheNonRattaches.set(chalandise, (e = new Set(chalandise.nonRattaches)));
    return e;
}

/** Libellé de la source des distances : « à pied » ou « à vol d'oiseau ». */
export const libelleDistance = (chalandise) => (chalandise?.source === 'osrm-foot' ? 'à pied' : "à vol d'oiseau");

const dejaIndexes = new WeakSet();
const cacheIndexes = new WeakMap();
/** Carreaux portant leur `index` (position dans le tableau d'origine du snapshot) ; mémoïsé, idempotent. */
export function avecIndex(carreaux) {
    if (dejaIndexes.has(carreaux)) return carreaux;
    let r = cacheIndexes.get(carreaux);
    if (!r) {
        r = carreaux.map((c, index) => ({...c, index}));
        dejaIndexes.add(r);
        cacheIndexes.set(carreaux, r);
    }
    return r;
}

const cle = (ix, iy) => `${ix}|${iy}`;
const caseDe = (v) => Math.floor(v / TAILLE_CASE);

/** Index spatial par case de 0,01° : Map<'ix|iy', élément[]> ; `lat` / `lng` lus sur chaque élément. */
function indexerParCase(elements) {
    const m = new Map();
    for (const e of elements) {
        if (e.lat == null || e.lng == null) continue;
        const k = cle(caseDe(e.lng), caseDe(e.lat));
        const l = m.get(k);
        if (l) l.push(e); else m.set(k, [e]);
    }
    return m;
}

const cacheCarreaux = new WeakMap();
function indexCarreaux(carreaux) {
    let m = cacheCarreaux.get(carreaux);
    if (!m) cacheCarreaux.set(carreaux, (m = indexerParCase(carreaux)));
    return m;
}

/** Visite les éléments des cases couvrant le disque de rayon `rayonM` autour de (lat, lng) ; `visiter` retourne true pour arrêter. */
function parcourirAutour(index, lat, lng, rayonM, visiter) {
    const dLat = rayonM / M_PAR_DEGRE_LAT;
    const dLng = rayonM / (M_PAR_DEGRE_LAT * Math.max(0.01, Math.cos(lat * Math.PI / 180)));
    for (let ix = caseDe(lng - dLng); ix <= caseDe(lng + dLng); ix++) {
        for (let iy = caseDe(lat - dLat); iy <= caseDe(lat + dLat); iy++) {
            const l = index.get(cle(ix, iy));
            if (l && l.some((e) => visiter(e))) return;
        }
    }
}

const estActive = (p) => p.statut !== 'RETIRÉ' && p.lat != null && p.lng != null;

/** Σ `pop` des carreaux dont le centre est à moins de `rayonM` du PAV. `null` si le PAV n'a pas de coordonnées. */
export function populationAPortee(pav, carreaux, rayonM = RAYON_DEFAUT, chalandise = null) {
    if (pav.lat == null || pav.lng == null) return null;
    carreaux = avecIndex(carreaux);
    let somme = 0;
    parcourirAutour(indexCarreaux(carreaux), pav.lat, pav.lng, rayonM, (c) => {
        if (distancePav(pav, c, c.index, chalandise) < rayonM) somme += c.pop;
        return false;
    });
    return somme;
}

/**
 * D1' et D1'' pour tous les PAV d'un coup. Retourne Map<id, {populationAPortee, populationAllouee, nbBornesAPortee, partAllouee}>
 * (valeurs null pour un PAV sans coordonnées). Égalité de distance : la borne rencontrée en premier.
 */
export function allouerPopulation(pavs, carreaux, rayonM = RAYON_DEFAUT, chalandise = null) {
    carreaux = avecIndex(carreaux);
    const actives = pavs.filter(estActive);
    const bornes = indexerParCase(actives);
    const alloue = new Map(actives.map((p) => [p.id, 0]));
    for (const c of carreaux) {
        let meilleure = null;
        let dMin = rayonM;
        parcourirAutour(bornes, c.lat, c.lng, rayonM, (p) => {
            const d = distancePav(p, c, c.index, chalandise);
            if (d < dMin) { dMin = d; meilleure = p; }
            return false;
        });
        if (meilleure) alloue.set(meilleure.id, alloue.get(meilleure.id) + c.pop);
    }
    const res = new Map();
    for (const p of pavs) {
        if (p.lat == null || p.lng == null) {
            res.set(p.id, {populationAPortee: null, populationAllouee: null, nbBornesAPortee: null, partAllouee: null});
            continue;
        }
        const aPortee = populationAPortee(p, carreaux, rayonM, chalandise);
        let autres = 0;
        parcourirAutour(bornes, p.lat, p.lng, rayonM, (q) => {
            if (q.id !== p.id && distanceM(p.lat, p.lng, q.lat, q.lng) < rayonM) autres++;
            return false;
        });
        const allouee = alloue.get(p.id) ?? 0;
        res.set(p.id, {populationAPortee: aPortee, populationAllouee: allouee, nbBornesAPortee: autres, partAllouee: aPortee > 0 ? allouee / aPortee : 0});
    }
    return res;
}

/** D5 : carreaux de plus de `seuilPop` habitants sans aucune borne (PAV non retiré, géolocalisé) à moins de `rayonM`. */
export function carreauxSansBorne(carreaux, pavs, {seuilPop = 150, rayonM = 400, chalandise = null} = {}) {
    carreaux = avecIndex(carreaux);
    const bornes = indexerParCase(pavs.filter((p) => p.statut !== 'RETIRÉ'));
    const nonRattaches = ensembleNonRattaches(chalandise); // D9 : hors constats
    const trous = [];
    for (const c of carreaux) {
        if (!(c.pop > seuilPop) || nonRattaches.has(c.index)) continue;
        let proche = false;
        parcourirAutour(bornes, c.lat, c.lng, rayonM, (p) => {
            if (distancePav(p, c, c.index, chalandise) < rayonM) proche = true;
            return proche;
        });
        if (!proche) trous.push(c);
    }
    return trous;
}

function dansAnneau(x, y, anneau) {
    let dedans = false;
    for (let i = 0, j = anneau.length - 1; i < anneau.length; j = i++) {
        const [xi, yi] = anneau[i];
        const [xj, yj] = anneau[j];
        if ((yi > y) !== (yj > y) && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi) dedans = !dedans;
    }
    return dedans;
}

const polygones = (f) => (f.geometry.type === 'Polygon' ? [f.geometry.coordinates] : f.geometry.coordinates);

/** Nom (propriété `nom`) de la commune du GeoJSON qui contient le point, sinon null. */
export function communeDuPoint(lng, lat, communes) {
    for (const f of communes.features) {
        for (const p of polygones(f)) {
            if (dansAnneau(lng, lat, p[0]) && !p.slice(1).some((trou) => dansAnneau(lng, lat, trou))) return f.properties.nom;
        }
    }
    return null;
}

/** Centre de l'emprise d'une commune du GeoJSON : {lat, lng} (null si inconnue). */
export function centreCommune(nom, communes) {
    const f = communes.features.find((x) => x.properties.nom === nom);
    if (!f) return null;
    let minX = Infinity; let maxX = -Infinity; let minY = Infinity; let maxY = -Infinity;
    for (const p of polygones(f)) {
        for (const [x, y] of p[0]) {
            minX = Math.min(minX, x); maxX = Math.max(maxX, x); minY = Math.min(minY, y); maxY = Math.max(maxY, y);
        }
    }
    return {lat: (minY + maxY) / 2, lng: (minX + maxX) / 2};
}

/**
 * D5' : trous de maillage, limités aux communes ÉQUIPÉES (au moins une borne active, clé de commune normalisée).
 * Les carreaux des communes sans aucune borne ne sont pas des « trous » : ces communes sont listées à part (`horsParc`).
 * @returns {{nb: number, parCommune: {nom: string, nb: number}[], carreaux: object[], horsParc: string[]}}
 */
export function trousDeMaillage(carreaux, pavs, communes, options) { // options : {seuilPop, rayonM, chalandise}
    carreaux = avecIndex(carreaux);
    const equipees = new Set();
    for (const p of pavs) {
        if (p.statut === 'RETIRÉ') continue;
        const cle = p.communeCle !== undefined ? p.communeCle : cleCommune(p.commune);
        if (cle !== null) equipees.add(cle);
    }
    const trous = [];
    const parNom = new Map();
    for (const c of carreauxSansBorne(carreaux, pavs, options)) {
        const nom = communeDuPoint(c.lng, c.lat, communes);
        if (nom === null || !equipees.has(cleCommune(nom))) continue;
        trous.push(c);
        parNom.set(nom, (parNom.get(nom) || 0) + 1);
    }
    const parCommune = [...parNom].map(([nom, nb]) => ({nom, nb})).sort((a, b) => b.nb - a.nb || a.nom.localeCompare(b.nom, 'fr'));
    const horsParc = communes.features.map((f) => f.properties.nom).filter((nom) => !equipees.has(cleCommune(nom))).sort((a, b) => a.localeCompare(b, 'fr'));
    return {nb: trous.length, parCommune, carreaux: trous, horsParc};
}

/** Corrélation de Pearson de deux séries de même longueur ; `null` si moins de 2 points ou variance nulle. */
export function pearson(xs, ys) {
    const n = xs.length;
    if (n < 2 || ys.length !== n) return null;
    const mx = xs.reduce((a, b) => a + b, 0) / n;
    const my = ys.reduce((a, b) => a + b, 0) / n;
    let sxy = 0; let sxx = 0; let syy = 0;
    for (let i = 0; i < n; i++) {
        sxy += (xs[i] - mx) * (ys[i] - my);
        sxx += (xs[i] - mx) ** 2;
        syy += (ys[i] - my) ** 2;
    }
    return sxx === 0 || syy === 0 ? null : sxy / Math.sqrt(sxx * syy);
}

/** Seuils de quantiles (20/40/60/80 % par défaut) d'une liste de valeurs : n seuils croissants. */
export function seuilsQuantiles(valeurs, fractions = [0.2, 0.4, 0.6, 0.8]) {
    if (valeurs.length === 0) return fractions.map(() => 0);
    const tri = [...valeurs].sort((a, b) => a - b);
    return fractions.map((f) => tri[Math.min(tri.length - 1, Math.floor(f * tri.length))]);
}

/** Rang de palier (0 à seuils.length) : nombre de seuils <= valeur. */
export const rangPalier = (valeur, seuils) => seuils.reduce((r, s) => (valeur >= s ? r + 1 : r), 0);

/** Rampe séquentielle bleu clair -> navy (5 paliers), commune aux calques Densité et Chalandise. */
export const RAMPE_POPULATION = ['#DCE9F6', '#A9C6E6', '#6E9BD0', '#3D64A8', '#213067'];

/**
 * D4' : nuage population allouée × tonnage. Une entrée par PAV ayant au moins une COLLECTE (simulée) et une population
 * allouée ; kg par semaine = kg total / durée de la période de collecte (de la première à la dernière collecte, tous PAV
 * confondus, en semaines) ; `groupe` restreint le nuage (et la corrélation) aux PAV de ce groupe ; tonnage mensuel moyen = kg par semaine × 52 / 12 ; remplissage moyen = moyenne des taux des COLLECTE.
 */
export function nuagePopulationTonnage(index, {groupe = null} = {}) {
    const MS_JOUR = 86400000;
    const ms = (iso) => { const [y, m, d] = iso.split('-').map(Number); return Date.UTC(y, m - 1, d); };
    const parPav = new Map();
    let min = null; let max = null;
    for (const [pavId, evts] of index.evenementsParPav) {
        for (const e of evts) {
            if (e.type !== 'COLLECTE') continue;
            const a = parPav.get(pavId) || {kg: 0, taux: []};
            a.kg += (e.detail && e.detail.tonnageKg) || 0;
            if (e.detail && typeof e.detail.tauxRemplissage === 'number') a.taux.push(e.detail.tauxRemplissage);
            parPav.set(pavId, a);
            if (min === null || e.date < min) min = e.date;
            if (max === null || e.date > max) max = e.date;
        }
    }
    if (min === null) return {points: [], semaines: 0, r: null};
    const semaines = ((ms(max) - ms(min)) / MS_JOUR + 1) / 7;
    const points = [];
    for (const [pavId, a] of parPav) {
        const pav = index.pavById.get(pavId);
        if (!pav || pav.populationAllouee == null || (groupe !== null && pav.groupe !== groupe)) continue;
        const kgSemaine = a.kg / semaines;
        points.push({
            pavId, nomenclature: pav.nomenclature, pop: pav.populationAllouee, kgSemaine, kgMois: (kgSemaine * 52) / 12,
            remplissage: a.taux.length > 0 ? a.taux.reduce((x, y) => x + y, 0) / a.taux.length : null,
        });
    }
    return {points, semaines, r: pearson(points.map((p) => p.pop), points.map((p) => p.kgMois))};
}

const nbFr = (x, d = 0) => x.toLocaleString('fr-FR', {maximumFractionDigits: d, minimumFractionDigits: d});

export const SEUILS_USAGE = {redondanteHab: 100, redondanteAutres: 2, pressionHab: 600, pressionRemplissage: CIBLE_REMPLISSAGE};

/**
 * D4' : « Redondance et pression », PARC AÉRIEN seulement (groupe `extra`, bornes actives). Population allouée et voisines
 * calculées entre bornes extra (cf. `allouerPopulation` par groupe).
 *  - zone propre réduite : >= 2 autres bornes extra à portée ET < 100 hab. alloués ;
 *  - sous pression : > 600 hab. alloués ET remplissage moyen > CIBLE_REMPLISSAGE (33 %, collectes simulées ; sans collecte, jamais sous pression).
 */
export function maillageEtUsage(index, seuils = SEUILS_USAGE) {
    const actives = index.pavs.filter((p) => p.groupe === 'extra' && p.statut !== 'RETIRÉ' && p.populationAllouee != null);
    const nuage = nuagePopulationTonnage(index, {groupe: 'extra'});
    const parPav = new Map(nuage.points.map((p) => [p.pavId, p]));
    const ligne = (p) => {
        const u = parPav.get(p.id);
        return {pavId: p.id, nomenclature: p.nomenclature, commune: p.communeLibelle ?? p.commune ?? '', populationAllouee: p.populationAllouee, nbBornesAPortee: p.nbBornesAPortee, kgSemaine: u ? u.kgSemaine : null, remplissage: u ? u.remplissage : null};
    };
    const parNomenclature = (a, b) => (a.nomenclature < b.nomenclature ? -1 : a.nomenclature > b.nomenclature ? 1 : 0);
    const zoneReduite = actives
        .filter((p) => p.nbBornesAPortee >= seuils.redondanteAutres && p.populationAllouee < seuils.redondanteHab)
        .map(ligne).sort((a, b) => a.populationAllouee - b.populationAllouee || b.nbBornesAPortee - a.nbBornesAPortee || parNomenclature(a, b));
    const sousPression = actives
        .filter((p) => p.populationAllouee > seuils.pressionHab && (parPav.get(p.id)?.remplissage ?? -1) > seuils.pressionRemplissage)
        .map(ligne).sort((a, b) => b.populationAllouee - a.populationAllouee || parNomenclature(a, b));
    return {rayonM: index.rayon, nbBornes: actives.length, zoneReduite, sousPression, nuage, seuils};
}

/** Phrase de constat de la carte « Redondance et pression ». */
export function constatUsage(r) {
    const s = r.seuils;
    const n = r.zoneReduite.length;
    const m = r.sousPression.length;
    return `Sur le parc aérien (${nbFr(r.nbBornes)} borne${r.nbBornes > 1 ? 's' : ''}), ${nbFr(n)} borne${n > 1 ? 's ont' : ' a'} une zone propre réduite (≥ ${s.redondanteAutres} autres bornes aériennes à portée et moins de ${nbFr(s.redondanteHab)} hab. alloués), ${nbFr(m)} ${m > 1 ? 'sont' : 'est'} sous pression (> ${nbFr(s.pressionHab)} hab. alloués et remplissage moyen > ${nbFr(s.pressionRemplissage * 100)} %).`;
}

const cacheCommunesCarreaux = new WeakMap();
/** Commune (nom du GeoJSON) de chaque carreau, mémorisée par jeu de carreaux et de communes. */
function communesDesCarreaux(carreaux, communes) {
    let parCommunes = cacheCommunesCarreaux.get(carreaux);
    if (!parCommunes) cacheCommunesCarreaux.set(carreaux, (parCommunes = new WeakMap()));
    let noms = parCommunes.get(communes);
    if (!noms) parCommunes.set(communes, (noms = carreaux.map((c) => communeDuPoint(c.lng, c.lat, communes))));
    return noms;
}

const RAYONS_RECHERCHE = [250, 500, 1000, 2000, 5000];

/** Distance (m) à la borne la plus proche du centre du carreau ; recherche par rayons croissants, plafonnée à 5 km. */
function distanceBornePlusProche(index, c) {
    let dMin = Infinity;
    for (const r of RAYONS_RECHERCHE) {
        parcourirAutour(index, c.lat, c.lng, r, (p) => {
            const d = distanceM(c.lat, c.lng, p.lat, p.lng);
            if (d < dMin) dMin = d;
            return false;
        });
        if (dMin < r) return dMin;
    }
    return dMin === Infinity ? RAYONS_RECHERCHE[RAYONS_RECHERCHE.length - 1] : dMin;
}

/** Médiane pondérée (premier élément dont le cumul des poids atteint la moitié du total) de [{valeur, poids}]. */
function medianePonderee(items) {
    const tri = items.filter((i) => i.poids > 0).sort((a, b) => a.valeur - b.valeur);
    const total = tri.reduce((a, i) => a + i.poids, 0);
    if (total === 0) return null;
    let cumul = 0;
    for (const i of tri) {
        cumul += i.poids;
        if (cumul >= total / 2) return i.valeur;
    }
    return tri[tri.length - 1].valeur;
}

/**
 * Accessibilité par commune ÉQUIPÉE (au moins une borne active), toutes bornes confondues (tous groupes, source réelle).
 * Pour chaque commune : `habitants` (Σ carreaux de la commune), `part250` / `part100` (part des habitants dont le carreau a
 * son centre à moins de 250 / 100 m d'une borne active), `bornesPour1000` (bornes actives de la commune / habitants x 1 000),
 * `distanceMediane` (médiane, pondérée par la population, de la distance du centre du carreau à la borne active la plus proche).
 * @returns {{communes: object[], territoire: object}} communes triées par `part250` croissante ; `territoire` = même mesure
 *          sur l'ensemble des communes équipées.
 */
export function accessibiliteParCommune(index, carreaux, communes) {
    const bornes = index.pavs.filter(estActive);
    const parBornes = indexerParCase(bornes);
    const noms = communesDesCarreaux(carreaux, communes);
    const equipees = new Map(); // clé -> nombre de bornes actives
    for (const p of bornes) {
        const cle = p.communeCle !== undefined ? p.communeCle : cleCommune(p.commune);
        if (cle !== null) equipees.set(cle, (equipees.get(cle) || 0) + 1);
    }
    const acc = new Map();
    const nonRattaches = ensembleNonRattaches(index.chalandise); // D9 : hors constats par commune
    carreaux = avecIndex(carreaux);
    carreaux.forEach((c, i) => {
        const nom = noms[i];
        if (nom === null || !equipees.has(cleCommune(nom)) || nonRattaches.has(c.index)) return;
        const d = distanceBornePlusProche(parBornes, c);
        const a = acc.get(nom) || {nom, cle: cleCommune(nom), habitants: 0, p250: 0, p100: 0, items: []};
        a.habitants += c.pop;
        if (d < 250) a.p250 += c.pop;
        if (d < 100) a.p100 += c.pop;
        a.items.push({valeur: d, poids: c.pop});
        acc.set(nom, a);
    });
    const mesure = (a, nbBornes) => ({
        habitants: a.habitants,
        part250: a.habitants > 0 ? a.p250 / a.habitants : null,
        part100: a.habitants > 0 ? a.p100 / a.habitants : null,
        bornesPour1000: a.habitants > 0 ? (nbBornes / a.habitants) * 1000 : null,
        distanceMediane: medianePonderee(a.items),
        nbBornes,
    });
    const tous = {habitants: 0, p250: 0, p100: 0, items: []};
    let nbBornesTous = 0;
    const lignes = [];
    for (const a of acc.values()) {
        const nb = equipees.get(a.cle);
        tous.habitants += a.habitants; tous.p250 += a.p250; tous.p100 += a.p100;
        for (const it of a.items) tous.items.push(it);
        nbBornesTous += nb;
        lignes.push({nom: a.nom, ...mesure(a, nb)});
    }
    lignes.sort((x, y) => (x.part250 ?? 0) - (y.part250 ?? 0) || x.nom.localeCompare(y.nom, 'fr'));
    return {communes: lignes, territoire: mesure(tous, nbBornesTous)};
}

/** Constat d'accessibilité : part territoire, communes la mieux et la moins dotée en bornes, 3 communes les moins desservies. */
export function constatAccessibilite(r, {nbMoinsDesservies = 3} = {}) {
    const t = r.territoire;
    if (r.communes.length === 0 || t.part250 === null) return 'Aucune commune équipée : accessibilité non calculable.';
    const pct = (x) => `${nbFr(x * 100)} %`;
    const dotees = r.communes.filter((c) => c.bornesPour1000 !== null).sort((a, b) => b.bornesPour1000 - a.bornesPour1000);
    const densite = dotees.length > 1
        ? `${dotees[0].nom} ${nbFr(dotees[0].bornesPour1000, 1)} bornes pour 1 000 habitants, ${dotees[dotees.length - 1].nom} ${nbFr(dotees[dotees.length - 1].bornesPour1000, 1)}`
        : `${nbFr(t.bornesPour1000, 1)} bornes pour 1 000 habitants`;
    const moins = r.communes.slice(0, nbMoinsDesservies).map((c) => `${c.nom} (${pct(c.part250)})`).join(', ');
    return `${pct(t.part250)} des habitants des communes équipées sont à moins de 250 m d'une borne ; ${densite} ; les communes les moins desservies : ${moins}.`;
}

/** Phrase de constat des carreaux denses non desservis (communes équipées uniquement) ; `chalandise` : source des distances. */
export function constatMaillage(r, {seuilPop = 150, rayonM = 400, chalandise = null} = {}) {
    const dist = libelleDistance(chalandise);
    if (r.nb === 0) return `Aucun carreau dense non desservi (plus de ${seuilPop} habitants, aucune borne active à moins de ${rayonM} m ${dist}) dans les communes équipées.`;
    const tete = r.parCommune[0];
    return `${nbFr(r.nb)} carreau${r.nb > 1 ? 'x denses non desservis' : ' dense non desservi'} (plus de ${seuilPop} habitants, aucune borne active à moins de ${rayonM} m ${dist}) dans les communes équipées${tete ? `, dont ${nbFr(tete.nb)} à ${tete.nom}` : ''}.`;
}

/**
 * Lot 13, carte « Vol d'oiseau ou à pied » : par commune ÉQUIPÉE, part des habitants dont le carreau a son centre à moins de
 * `seuilM` (150 m) d'une borne active (tous groupes), mesurée à vol d'oiseau (`partVol`) et à pied (`partPied`, distances OSRM
 * du snapshot) ; `ecart` = (partVol - partPied) x 100, en points. Triée par écart décroissant. Sans distances à pied
 * (`chalandise.source !== 'osrm-foot'`) : `{disponible: false}`, rien n'est calculé.
 * @returns {{disponible: boolean, seuilM: number, communes: object[], territoire: object|null}}
 */
export function ecartVolOiseauPied(index, carreaux, communes, {seuilM = 150} = {}) {
    const chalandise = index.chalandise ?? null;
    if (chalandise?.source !== 'osrm-foot') return {disponible: false, seuilM, communes: [], territoire: null};
    carreaux = avecIndex(carreaux);
    const bornes = index.pavs.filter(estActive);
    const parBornes = indexerParCase(bornes);
    const noms = communesDesCarreaux(carreaux, communes);
    const equipees = new Set();
    for (const p of bornes) {
        const cle = p.communeCle !== undefined ? p.communeCle : cleCommune(p.commune);
        if (cle !== null) equipees.add(cle);
    }
    const acc = new Map();
    const tous = {habitants: 0, vol: 0, pied: 0};
    const nonRattaches = ensembleNonRattaches(chalandise); // D9 : exclus du numérateur et du dénominateur
    carreaux.forEach((c, i) => {
        const nom = noms[i];
        if (nom === null || !equipees.has(cleCommune(nom)) || nonRattaches.has(c.index)) return;
        let vol = false;
        let pied = false;
        parcourirAutour(parBornes, c.lat, c.lng, seuilM, (p) => {
            if (distanceM(c.lat, c.lng, p.lat, p.lng) < seuilM) vol = true;
            if (distancePav(p, c, c.index, chalandise) < seuilM) pied = true;
            return vol && pied;
        });
        const a = acc.get(nom) || {nom, habitants: 0, vol: 0, pied: 0};
        a.habitants += c.pop;
        tous.habitants += c.pop;
        if (vol) { a.vol += c.pop; tous.vol += c.pop; }
        if (pied) { a.pied += c.pop; tous.pied += c.pop; }
        acc.set(nom, a);
    });
    const mesure = (a) => {
        const partVol = a.habitants > 0 ? a.vol / a.habitants : null;
        const partPied = a.habitants > 0 ? a.pied / a.habitants : null;
        return {habitants: a.habitants, partVol, partPied, ecart: partVol === null ? null : (partVol - partPied) * 100};
    };
    const lignes = [...acc.values()].map((a) => ({nom: a.nom, ...mesure(a)}))
        .sort((x, y) => (y.ecart ?? -Infinity) - (x.ecart ?? -Infinity) || x.nom.localeCompare(y.nom, 'fr'));
    return {disponible: true, seuilM, communes: lignes, territoire: mesure(tous)};
}

/** Constat de la carte « Vol d'oiseau ou à pied ». */
export function constatEcartVolPied(r) {
    if (!r.disponible) return "Distances à pied non disponibles : seule la distance à vol d'oiseau est mesurée.";
    const t = r.territoire;
    if (r.communes.length === 0 || t.partVol === null) return 'Aucune commune équipée : écart non calculable.';
    const pct = (x) => `${nbFr(x * 100)} %`;
    const tete = r.communes[0];
    const fin = tete.ecart > 0 ? ` L'écart est le plus fort à ${tete.nom} (${nbFr(tete.ecart)} point${Math.round(tete.ecart) > 1 ? 's' : ''}).` : " Aucun écart entre les deux mesures.";
    return `À vol d'oiseau, ${pct(t.partVol)} des habitants sont à moins de ${r.seuilM} m d'une borne active ; à pied, ${pct(t.partPied)}.${fin}`;
}

/** Phrase de la ligne de comptage D9 ; vide si aucun carreau non rattaché. */
export function constatNonRattaches(r) {
    if (r.carreaux === 0) return null;
    return `${nbFr(r.carreaux)} carreau${r.carreaux > 1 ? 'x non rattachés' : ' non rattaché'} (${nbFr(r.habitants)} habitant${r.habitants > 1 ? 's' : ''}) : centre à plus de 100 m de toute voirie piétonne, hors constats par commune.`;
}

/** D9 : comptage des carreaux non rattachés (restent dans la population totale) : {carreaux, habitants}. */
export function comptageNonRattaches(index) {
    const set = ensembleNonRattaches(index.chalandise);
    let habitants = 0;
    let carreaux = 0;
    for (const c of index.carreaux) if (set.has(c.index)) { carreaux++; habitants += c.pop; }
    return {carreaux, habitants};
}

export const RAYON_DETOUR_VOL_M = 300; // D8 : une borne à moins de 300 m à vol d'oiseau...

/**
 * D8 « détour à pied » (source `osrm-foot` seulement, sinon `null`) : carreau hors D9 avec au moins une borne active à moins
 * de 300 m à vol d'oiseau et aucune paire dans `chalandise.distances` (aucune borne à <= `rayonMaxM`, 400 m, à pied).
 * `communes` (GeoJSON) donne la commune du carreau ; à défaut, la commune de la borne la plus proche.
 * @returns {{carreaux: {index, pop, commune, nbPavMoins300, pavProche: {id, nomenclature, distanceM}}[], parCommune: {commune, carreaux, habitants}[], total: {carreaux, habitants}}|null}
 */
export function detoursAPied(index, communes = null) {
    const chalandise = index.chalandise ?? null;
    if (chalandise?.source !== 'osrm-foot') return null;
    const nonRattaches = ensembleNonRattaches(chalandise);
    const avecPaire = new Set();
    for (const paires of Object.values(chalandise.distances ?? {})) for (const k of Object.keys(paires)) avecPaire.add(Number(k));
    const bornes = indexerParCase(index.pavs.filter(estActive));
    const carreaux = [];
    for (const c of avecIndex(index.carreaux)) {
        if (nonRattaches.has(c.index) || avecPaire.has(c.index)) continue;
        let nb = 0;
        let proche = null;
        let dMin = Infinity;
        parcourirAutour(bornes, c.lat, c.lng, RAYON_DETOUR_VOL_M, (p) => {
            const d = distanceM(c.lat, c.lng, p.lat, p.lng);
            if (d < RAYON_DETOUR_VOL_M) { nb++; if (d < dMin) { dMin = d; proche = p; } }
            return false;
        });
        if (nb === 0) continue;
        const commune = (communes ? communeDuPoint(c.lng, c.lat, communes) : null) ?? proche.communeLibelle ?? proche.commune ?? '';
        carreaux.push({index: c.index, pop: c.pop, commune, nbPavMoins300: nb, pavProche: {id: proche.id, nomenclature: proche.nomenclature, distanceM: dMin}});
    }
    const parNom = new Map();
    const total = {carreaux: 0, habitants: 0};
    for (const c of carreaux) {
        const a = parNom.get(c.commune) || {commune: c.commune, carreaux: 0, habitants: 0};
        a.carreaux++; a.habitants += c.pop;
        parNom.set(c.commune, a);
        total.carreaux++; total.habitants += c.pop;
    }
    const parCommune = [...parNom.values()].sort((a, b) => b.habitants - a.habitants || a.commune.localeCompare(b.commune, 'fr'));
    carreaux.sort((a, b) => b.pop - a.pop || a.index - b.index);
    return {carreaux, parCommune, total};
}

const cacheMarquages = new WeakMap();
const MARQUAGES_VIDES = {actif: false, detours: new Set(), nonRattaches: new Set()};
/**
 * Marquage du calque densité (lot 13) : Sets d'index des carreaux D8 (« détour à pied », detoursAPied) et D9 (index.nonRattaches).
 * Vides et actif=false hors source osrm-foot. Mémoïsé sur index (un seul calcul par jeu de données).
 * @returns {{actif: boolean, detours: Set<number>, nonRattaches: Set<number>}}
 */
export function marquagesCarreaux(index) {
    if (!index || index.chalandise?.source !== 'osrm-foot') return MARQUAGES_VIDES;
    let m = cacheMarquages.get(index);
    if (!m) {
        const d = detoursAPied(index);
        m = {actif: true, detours: new Set((d?.carreaux ?? []).map((c) => c.index)), nonRattaches: new Set(ensembleNonRattaches(index.chalandise))};
        cacheMarquages.set(index, m);
    }
    return m;
}

/** Constat de la carte « Détours à pied ». */
export function constatDetours(r) {
    if (r === null) return 'Distances à pied non disponibles : le détour à pied ne peut pas être mesuré.';
    if (r.total.carreaux === 0) return "Aucun habitant n'a une borne à moins de 300 m à vol d'oiseau sans en avoir une à moins de 400 m à pied.";
    return `${nbFr(r.total.habitants)} habitant${r.total.habitants > 1 ? 's ont' : ' a'} une borne à moins de 300 m à vol d'oiseau mais aucune à moins de 400 m à pied.`;
}

/** Grille rurale : `true` si la population allouée À 150 m (`populationAllouee150`, indépendante du curseur) dépasse `SEUIL_RURAL_HAB` (null si non alloué). */
export const depasseSeuilRural = (pav) => (pav.populationAllouee150 == null ? null : pav.populationAllouee150 > SEUIL_RURAL_HAB);

/** Ligne « Hors parc : … » (communes sans aucune borne active) ; null si toutes les communes sont équipées. */
export function ligneHorsParc(r) {
    return r.horsParc && r.horsParc.length > 0 ? `Hors parc : ${r.horsParc.join(', ')}` : null;
}

/**
 * Fiche PAV. Parc aérien (`extra`) : « 412 hab. · 2 autres bornes à portée » (population allouée, redondance du maillage).
 * Intramuros : « Maillage de proximité (N bornes à moins de 250 m) » (pas d'allocation par borne : bornes au pied des immeubles).
 */
export function libellePopulation(pav, rayonM = RAYON_DEFAUT) {
    if (pav.populationAllouee == null) return '—';
    const n = pav.nbBornesAPortee || 0;
    if (pav.groupe !== 'extra') return `Maillage de proximité (${nbFr(n)} borne${n > 1 ? 's' : ''} à moins de ${nbFr(rayonM)} m)`;
    const autres = n === 0 ? 'aucune autre borne à portée' : `${nbFr(n)} autre${n > 1 ? 's' : ''} borne${n > 1 ? 's' : ''} à portée`;
    return `${nbFr(Math.round(pav.populationAllouee))} hab. · ${autres}`;
}
